/**
 * Presence Context — REST Polling Based
 *
 * Architecture:
 *   POST /heartbeat every 15s  → registers liveness + current page
 *   GET  /online    every 10s  → fetches who's online
 *   visibilitychange API       → pauses heartbeats when tab hidden
 *   No WebSocket dependency    → zero connection-state bugs
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { authService } from '../services/authService';
import { useLocation } from 'react-router-dom';

const PresenceContext = createContext(null);

const HEARTBEAT_INTERVAL = 15000; // 15s
const POLL_INTERVAL = 10000;      // 10s
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PAGE_NAMES = {
  '/': 'Parts Catalogue',
  '/parts': 'Parts Catalogue',
  '/taxonomy': 'Taxonomy',
  '/inventory': 'Inventory',
  '/inventory/receiving': 'GRN Management',
  '/inventory/stock': 'Stock Inquiry',
  '/inventory/locations': 'Location Management',
  '/inventory/lots': 'Lot Management',
  '/inventory/issue-transfer': 'Issue/Transfer',
  '/inventory/cycle-counts': 'Cycle Counts',
  '/inventory/quarantine': 'Quarantine',
  '/inventory/ncr': 'NCR Management',
  '/inventory/adjustments': 'Adjustments',
  '/inventory/qc-dashboard': 'QC Dashboard',
  '/inventory/manager-dashboard': 'Manager Dashboard',
  '/admin/user': 'User Management',
  '/admin/permissions': 'Role Permissions',
  '/settings': 'Settings',
  '/settings/profile': 'Profile Settings',
  '/db-explorer': 'DB Explorer',
};

const getPageName = (pathname) => {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  if (pathname.startsWith('/parts/')) return 'Part Details';
  if (pathname.startsWith('/admin/user/')) return 'User Details';
  const segments = pathname.split('/').filter(Boolean);
  return segments.length > 0
    ? segments[segments.length - 1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : 'Dashboard';
};

export const PresenceProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const token = authService.getToken();
  const location = useLocation();

  const heartbeatRef = useRef(null);
  const pollRef = useRef(null);
  const isVisibleRef = useRef(true);
  const pageNameRef = useRef(getPageName(location.pathname));

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [stats, setStats] = useState({ online: 0, idle: 0, total: 0 });

  // Keep page ref in sync without triggering effect chains
  useEffect(() => {
    pageNameRef.current = getPageName(location.pathname);
  }, [location.pathname]);

  // Heartbeat — reads page from ref, stable callback
  const sendHeartbeat = useCallback(async () => {
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/v1/presence/heartbeat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ current_page: pageNameRef.current }),
      });
    } catch {
      // Silent — next heartbeat will retry
    }
  }, [token]);

  // Fetch online users
  const fetchOnline = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/v1/presence/online`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOnlineUsers(data.users || []);
        setStats(data.stats || { online: 0, idle: 0, total: 0 });
      }
    } catch {
      // Silent
    }
  }, [token]);

  // Main effect — start intervals when authenticated
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    // Register heartbeat first, THEN fetch — avoids "0 Online" flash on load
    sendHeartbeat().then(() => fetchOnline());

    heartbeatRef.current = setInterval(() => {
      if (isVisibleRef.current) sendHeartbeat();
    }, HEARTBEAT_INTERVAL);

    pollRef.current = setInterval(fetchOnline, POLL_INTERVAL);

    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(pollRef.current);
    };
  }, [isAuthenticated, token, sendHeartbeat, fetchOnline]);

  // Visibility change — pause heartbeats when hidden, resume on focus
  useEffect(() => {
    const handler = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden && token) {
        sendHeartbeat();
        fetchOnline();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [sendHeartbeat, fetchOnline, token]);

  // beforeunload — send offline signal via sendBeacon on tab close
  useEffect(() => {
    if (!token) return;
    const handler = () => {
      const blob = new Blob(
        [JSON.stringify({ token })],
        { type: 'application/json' }
      );
      navigator.sendBeacon(`${API_URL}/api/v1/presence/offline`, blob);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [token]);

  // Immediate heartbeat on page navigation
  useEffect(() => {
    if (isAuthenticated && token) {
      sendHeartbeat();
    }
  }, [location.pathname, isAuthenticated, token, sendHeartbeat]);

  const value = {
    isConnected: isAuthenticated,
    onlineUsers,
    stats,
    activeCount: stats.online || 0,
    idleCount: stats.idle || 0,
    awayCount: 0,
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};

export default PresenceContext;
